import {
  Survey,
  SurveyChoiceOptionType,
  SurveyElement,
  SurveyElementChoices,
  SurveyElementText,
  SurveyL10n
} from 'core/SurveyModels'

type SurveyL10nEnd = keyof SurveyL10n['lang']['page']['element']
type Nested<O> = {
  [K in keyof O]: Nested<O[K]> & {en?: string; ar?: string; he?: string}
}
type L10nSurveyElement = Nested<SurveyElementChoices & SurveyElementText>

export class SurveyTranslationSyncer {
  private static supportedLanguages = ['en', 'ar', 'he']

  constructor(
    private survey: Survey,
    private l10n: SurveyL10n
  ) {}

  updateSurvey(survey: Survey) {
    this.survey = survey
  }

  updateL10n(l10n: SurveyL10n) {
    this.l10n = l10n
  }

  // Get survey structure and apply l10n object if possible over it. It will return new valid l10n
  getUpdatedL10n(): [SurveyL10n, boolean] {
    const l10n: SurveyL10n = {}
    this.survey.pages.forEach((page) => {
      page.elements.forEach((element) => {
        SurveyTranslationSyncer.translatableProps(element, (prop, choice) => {
          SurveyTranslationSyncer.supportedLanguages.forEach((lang) => {
            if (
              (element as L10nSurveyElement)[prop]?.en ||
              (element as L10nSurveyElement)?.choices?.some(
                (c) => c.value === choice
              )
            ) {
              if (!l10n[lang]) l10n[lang] = {}
              if (!l10n[lang][page.name]) l10n[lang][page.name] = {}
              if (!l10n[lang][page.name][element.name])
                l10n[lang][page.name][element.name] = {}
              if (!choice) {
                // @ts-expect-error because of the L10nSurveyElement there is some problems when we try to assign value to prop
                l10n[lang][page.name][element.name][prop] =
                  this.extractDefaultValue(lang, page.name, element.name, prop)
              } else {
                if (!l10n[lang][page.name][element.name].choices) {
                  l10n[lang][page.name][element.name].choices = {}
                }
                l10n[lang][page.name][element.name].choices![choice] =
                  this.extractDefaultValue(
                    lang,
                    page.name,
                    element.name,
                    prop,
                    choice
                  )
              }
            }
          })
        })
      })
    })
    return [l10n, this.hasChanges(this.l10n, l10n)]
  }

  // Get percent progress - l10n against survey
  translationProgress(lang: string): number {
    let totalElements = 0
    let translatedElements = 0
    this.survey.pages.forEach((page) =>
      page.elements.forEach((element) => {
        SurveyTranslationSyncer.translatableProps(element, (prop, meta) => {
          totalElements++
          if (!meta) {
            translatedElements += this.l10n?.[lang]?.[page.name]?.[
              element.name
            ]?.[prop]
              ? 1
              : 0
          } else {
            translatedElements += this.l10n?.[lang]?.[page.name]?.[element.name]
              ?.choices?.[meta]
              ? 1
              : 0
          }
        })
      })
    )

    return Math.trunc((translatedElements / totalElements) * 100)
  }

  // Merge survey with l10n property for selected language
  getLocalizedSurvey(language: string) {
    return SurveyTranslationSyncer.localizeSurvey(
      language,
      this.survey,
      this.l10n
    )
  }

  // Merge survey with l10n property for selected language
  static localizeSurvey(language: string, survey: Survey, l10n: SurveyL10n) {
    const copy = JSON.parse(JSON.stringify(survey)) as unknown as Survey
    copy.pages.forEach((page) =>
      page.elements.forEach((element) => {
        SurveyTranslationSyncer.translatableProps(element, (prop, choice) => {
          if (choice) {
            ;(
              (element as L10nSurveyElement).choices as SurveyChoiceOptionType[]
            ).find((ch) => ch.value === choice)!.label[language] =
              l10n[language]?.[page.name]?.[element.name]?.choices?.[choice] ?? // desired language
              l10n['en']?.[page.name]?.[element.name]?.choices?.[choice] ?? // fallback to en
              (
                (element as L10nSurveyElement)
                  .choices as SurveyChoiceOptionType[]
              ).find((ch) => ch.value === choice)!.label['en'] // fallback to default label
          } else {
            ;(element as L10nSurveyElement)[prop]![
              language as 'en' | 'ar' | 'he'
            ] = (l10n[language]?.[page.name]?.[element.name]?.[prop] ?? // desired language
              l10n['en']?.[page.name]?.[element.name]?.[prop] ?? // fallback to en
              (element as L10nSurveyElement)[prop]!.en) as string // fallback to default label
          }
        })
      })
    )
    return copy
  }

  static fullLocalization(survey: Survey, l10n: SurveyL10n) {
    let copy = JSON.parse(JSON.stringify(survey)) as unknown as Survey
    SurveyTranslationSyncer.supportedLanguages.forEach((language) => {
      copy = SurveyTranslationSyncer.localizeSurvey(language, copy, l10n)
    })
    return copy
  }

  // Update common values such as errorText. Return true if some value has been changed
  updateCommonL10nValues(trFN: (key: string, lang: string) => string): boolean {
    let hasChanges = false
    Object.keys(this.l10n).forEach((lang) => {
      Object.keys(this.l10n[lang]).forEach((page) => {
        Object.keys(this.l10n[lang][page]).forEach((element) => {
          if (!this.l10n[lang][page][element].errorText) {
            this.l10n[lang][page][element].errorText = trFN(
              'common:fnd-common-widget-default-error-text',
              lang
            )
            hasChanges = true
          }
        })
      })
    })
    return hasChanges
  }

  private extractDefaultValue(
    lang: string,
    pageName: string,
    elementName: string,
    prop: SurveyL10nEnd,
    choice?: string | number
  ) {
    const pageIndex = this.survey.pages.findIndex(
      (page) => page.name === pageName
    )
    const elementIndex = this.survey.pages[pageIndex].elements.findIndex(
      (el) => el.name === elementName
    )
    const element = this.survey.pages[pageIndex].elements[elementIndex]

    if (!choice) {
      const phrase = (this.l10n?.[lang]?.[pageName]?.[elementName]?.[prop] ??
        '') as string
      return lang === 'en' && !phrase
        ? ((element as L10nSurveyElement)[prop]!.en as string)
        : phrase
    }
    const phrase =
      this.l10n?.[lang]?.[pageName]?.[elementName]?.choices?.[choice] ?? ''
    return lang === 'en' && !phrase
      ? ((element as SurveyElementChoices).choices?.find(
          (c) => c.value === choice
        )?.label?.en as string)
      : phrase
  }

  private flatObject<O extends object>(input: O): Record<string, string> {
    const flat = (
      flattenedObject: Record<string, string>,
      key: string,
      value: Record<string, string>,
      previousPrefix = ''
    ): Record<string, string> => {
      const prefix = [previousPrefix, key].filter((v) => v).join('.')
      return typeof value === 'object'
        ? Object.keys(value).reduce(
            (prev, curr) =>
              flat(
                prev,
                curr,
                value[curr] as unknown as Record<string, string>,
                prefix
              ),
            flattenedObject
          )
        : Object.assign(flattenedObject, {[prefix]: value})
    }
    return (Object.keys(input) as Array<keyof typeof input>).reduce(
      (prev, curr) =>
        flat(prev, curr as string, input[curr] as Record<string, string>),
      {}
    )
  }

  private hasChanges(l1: SurveyL10n, l2: SurveyL10n) {
    const f1 = this.flatObject(l1)
    const f2 = this.flatObject(l2)
    const a1 = Object.keys(f1)
    const a2 = Object.keys(f2)
    const diff = a1
      .filter((x) => !a2.includes(x))
      .concat(a2.filter((x) => !a1.includes(x)))
    return diff.length > 0
  }

  private static translatableProps(
    element: SurveyElement,
    cb: (prop: SurveyL10nEnd, choice?: string | number) => void
  ) {
    if (element.title?.en) {
      cb('title')
    }
    if (element.errorText?.en) {
      cb('errorText')
    }
    if (element.hint?.en) {
      cb('hint')
    }
    if ((element as SurveyElementText).placeholder?.en) {
      cb('placeholder')
    }
    if (['dropdown', 'choices', 'price-modifier'].includes(element.type)) {
      ;(element as SurveyElementChoices).choices?.forEach((choice) => {
        cb('choices', choice.value)
      })
    }
  }
}
